import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, ExternalLink, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import { useQuery } from '@apollo/client';
import { gql } from '@/gql/gql';
import { Logo } from '../../components/logos/logo';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const GET_MERCHANT_ID = gql(/* GraphQL */ `
  query GetMerchantId {
    currentMerchant {
      id
      domain
    }
  }
`);

interface ConfigurePixelProps {
  /**
   * Text to display on the footer button.
   * Defaults to 'Complete Setup'.
   */
  buttonText?: string;

  /**
   * Click handler for the footer button.
   * If not provided, defaults to marking the step as completed and navigating to the home page.
   */
  onButtonClick?: () => void;

  /**
   * Controls the visibility of the footer section.
   * Defaults to true.
   */
  showFooter?: boolean;

  /**
   * Text for the header.
   * Defaults to 'Now let's get Contextual enabled in Shopify.'
   */
  headerText?: string;
}

export function ConfigurePixel({
  buttonText = 'Complete Setup',
  onButtonClick,
  showFooter = true
}: ConfigurePixelProps) {
  const { data } = useQuery(GET_MERCHANT_ID);

  const merchantId = data?.currentMerchant.id?.split('.')[0] ?? '';
  const adminSettingsUrl = `https://admin.shopify.com/store/${merchantId}/settings/customer_events`;

  const navigate = useNavigate();

  const handleButtonClick = () => {
    if (onButtonClick) {
      onButtonClick();
    } else {
      void navigate('../setup/complete-setup');
    }
  };

  const getCustomPixelScript = () => {
    const domain = data?.currentMerchant.domain ?? 'your-shop-domain';

    return `
const GTM_CONTAINER_ID = 'GTM-XXXXXXXX';
const load = (host) =>
  import(host + "/worker-sdk/web-pixel").then((pixel) => ({ pixel, host }));

const execute = async (api, gtmContainerId) => {
  // Falls back to Contextual's shared endpoint if ctx.${domain} is not set up.
  const { pixel, host } = await load("https://ctx.${domain}").catch(() =>
    load("https://merchants.getcontextual.io")
  );
  await pixel.contextualWebAppPixel(api, gtmContainerId, { host });
};

execute({ analytics, browser, init }, GTM_CONTAINER_ID);
    `.trim();
  };

  const copyToClipboard = async (text: string) => {
    if (data?.currentMerchant.domain) {
      await navigator.clipboard.writeText(text);
      toast({
        title: 'Copied to clipboard!',
        variant: 'default'
      });
    } else {
      toast({
        title: 'Unable to copy script',
        description: 'Shop domain is not available. Please try again later.',
        variant: 'destructive'
      });
    }
  };

  return (
    <Card className="w-full max-w-xl mx-4 sm:mx-auto p-9 rounded-[1rem]">
      <ArrowLeft
        className="cursor-pointer"
        onClick={() => {
          void navigate(-1);
        }}
      />
      <CardHeader className="items-center space-y-8 p-0">
        <Logo icon="contextual" width={36} height={36} />
        <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-medium">
          Connect your store
        </h1>
      </CardHeader>
      <CardContent className="space-y-6 my-9 p-0">
        <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold">{`Paste Contextual's Custom Web Pixel snippet in Shopify Settings`}</h3>
        <Button
          variant="secondary"
          className="mt-2"
          onClick={() => window.open(adminSettingsUrl, '_blank')}
        >
          Shopify Store Admin Settings
          <ExternalLink className="ml-2 h-4 w-4" />
        </Button>
        <p className="text-sm text-gray-600 mt-2">
          In your Shopify Settings, go to{' '}
          <span className="font-semibold text-sm text-black">
            {`
  Shopify Store Admin > Settings > Customer Events > Add Custom Pixel. `}
          </span>
          Name your custom pixel &quot;Contextual&rdquo; and copy and paste the following snippet
          into the code editor.
        </p>
        <div className="flex space-x-3">
          <Input
            placeholder={getCustomPixelScript()}
            className="pr-20 rounded-md	border border-gray-300"
            disabled={true}
          />
          <Button
            variant="secondary"
            onClick={() => {
              void copyToClipboard(getCustomPixelScript());
            }}
          >
            <Copy className="mr-2 h-4 w-4" />
            Copy Script
          </Button>
        </div>
        <Alert className="mt-8" variant="warning">
          <TriangleAlert size={16} />
          <AlertTitle>Important!</AlertTitle>
          <AlertDescription>{`Please also replace the pasted placeholder Google Tag Manager (GTM) container ID (e.g. GTM-XXXXXXX) with the
            one you use for your store.`}</AlertDescription>
        </Alert>
        <p className="text-sm text-gray-600 mt-2">
          {`Click "save" after pasting in the code. Once this is complete, click "Connect" to connect your pixel to
              Contextual.`}
        </p>
      </CardContent>
      {showFooter && (
        <CardFooter className="flex justify-center space-x-8">
          <Button onClick={handleButtonClick}>{buttonText}</Button>
          <Button variant="secondary" className="">
            Cancel
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}
